import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { FaBoxOpen } from 'react-icons/fa';
import SpinLoader from '../components/SpinLoader.js';
import { toast } from 'react-toastify';
import useScrollToTop from '../components/useScrollToTop.js';
import { transformTrackingData } from '../Common/CalculateRate.js';
import { GET_TRACKING_DOMESTIC, TRACK_SHIPMENT } from '../graphql/Query/Advertise.js';
import { useLazyQuery } from '@apollo/client';


const validationSchema = Yup.object({
    AWBNumber: Yup.string()
        .required('AWB Number is required')
        .matches(/^[a-zA-Z0-9]+$/, 'AWB Number is invalid'),
});

const TrackForm = ({ setTrackingInfo, setModalOpen, awb }) => {
    useScrollToTop();

    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [shipmentType, setShipmentType] = useState('domestic');
    const [trackShipmentDomestic] = useLazyQuery(GET_TRACKING_DOMESTIC, {
        fetchPolicy: "cache-and-network",
    });
    const [getGlobalTracking, { loading: globalTrackingLoading }] = useLazyQuery(TRACK_SHIPMENT, {
        onCompleted: (data) => {
            setIsLoading(false);

            const courier = data?.trackShipment?.courier;
            const trackingData = data?.trackShipment;

            const transformed = transformTrackingData(courier, trackingData);

            if (transformed?.history?.length > 0) {
                setTrackingInfo(transformed); // now includes { history, details }
                setModalOpen(true);
            } else {
                toast.error('No tracking events found for this global shipment.');
                setError('No tracking events found.');
            }
        },
        onError: (err) => {
            setIsLoading(false);
            toast.error(`${err?.message}`);
            setError('No tracking events found.');
        },
    });

    const formik = useFormik({
        initialValues: {
            AWBNumber: awb || "",
        },
        validationSchema,
        onSubmit: async values => {
            const { AWBNumber } = values;
            setIsLoading(true);
            setError('');
            if (shipmentType === 'domestic') {
                try {
                    const { data } = await trackShipmentDomestic({ variables: { awb: AWBNumber } });
                    // console.log('✌️data --->', data);
                    if (data?.trackShipmentDomestic?.awb) {
                        const shipment = data?.trackShipmentDomestic;

                        if (shipment?.courier && shipment?.data) {
                            const transformed = transformTrackingData(shipment?.courier, shipment);

                            if (transformed?.history?.length > 0) {
                                setTrackingInfo(transformed);
                                setModalOpen(true);
                            } else {
                                toast.error('No tracking events found for this global shipment.');
                            }

                            return; // ✅ stop here, don’t continue domestic parsing
                        }

                        // Transform transit_history to expected structure
                        const parsedHistory = shipment?.transit_history?.map(scan => ({
                            event_time: scan?.event_time
                                ? new Date(Number(scan?.event_time)).toLocaleString()
                                : '',
                            location: scan?.location || '',
                            message: scan?.remarks || scan?.status || ''
                        }))?.reverse() || [];

                        const trackingInfo = {
                            details: {
                                altReferenceNumber: shipment?.forwardingNumber,
                                proof_of_delivery: shipment?.proof_of_delivery,
                                last_center_details: shipment?.last_center_details
                                // destination: shipment.last_center_details || '',
                                // weight: '',        // Provide logic if available
                                // weightUnit: '',    // Provide logic if available
                                // consignee_name: shipment?.consignee_name || '',
                                // consignee_phone: shipment?.consignee_phone || '',
                                // delivered_at: shipment?.delivered_at || '',
                                // createdAt: shipment?.order?.createdAt || '',
                                // francise: shipment?.order?.franciseId || null
                            },
                            history: parsedHistory
                        };

                        setTrackingInfo(trackingInfo);
                        setModalOpen(true);

                    } else {
                        toast.error("Tracking not found");
                    }
                } catch (error) {
                    if (error?.graphQLErrors?.length > 0) {
                        toast.error(error.graphQLErrors[0].message);
                    } else if (error?.networkError) {
                        toast.error("Network error. Please try again.");
                    } else {
                        toast.error("Something went wrong.");
                    }
                    setError('Error tracking shipment. Please try again.');
                } finally {
                    setIsLoading(false);
                }
            } else if (shipmentType === 'global') {
                getGlobalTracking({ variables: { trackingNumber: AWBNumber } });
            }
        }
    });

    useEffect(() => {
        if (awb) {
            formik.handleSubmit();
        }
    }, [awb]);
    return (
        <>
            <div className="lg:w-1/2 md:rounded-l-lg rounded-none md:py-8 md:px-9 p-6 bg-white ring-1 h-full ring-slate-900/5 shadow-md md:shadow-md">
                <form onSubmit={formik.handleSubmit}>
                    <div className='space-y-5'>
                        <div className="flex space-x-3 justify-between text-xl font-bold mb-7">
                            <div className='flex'>
                                <FaBoxOpen className="h-7 w-7" />
                                <h3 className="text-slate-800 ms-2">Can't find your delivery?</h3>
                            </div>
                            <p className='hidden lg:hidden xl:block md:block text-gray-400 font-normal text-sm'>Anytime, Anywhere</p>
                        </div>
                        <div>
                            <p className="text-slate-900 font-medium mb-2">Find your delivery using your Tracking AWB Number</p>
                        </div>
                        <div className="bg-white rounded-lg">
                            <div className="flex gap-2 border-b">
                                <button type="button" onClick={() => {
                                    setShipmentType('domestic');
                                    formik.setFieldValue('AWBNumber', ''); // Clear AWB field
                                    setError(''); // Optional: Clear any previous errors
                                }} className={`px-4 py-2 text-sm ${shipmentType === 'domestic' ? 'text-black bg-blue-100 rounded-t-md border-b-2 border-black font-semibold' : 'text-gray-600 hover:text-black'}`}>
                                    India's Shipment
                                </button>

                                <button type="button"
                                    onClick={() => {
                                        setShipmentType('global');
                                        formik.setFieldValue('AWBNumber', ''); // Clear AWB field
                                        setError(''); // Optional: Clear any previous errors
                                    }} className={`px-4 py-2 text-sm ${shipmentType === 'global' ? 'text-black bg-blue-100 rounded-t-md border-b-2 border-black font-semibold' : 'text-gray-600 hover:text-black'}`}
                                >
                                    Global Shipment
                                </button>
                            </div>
                        </div>
                        <p className='text-md font-semibold'>Tracking (AWB) Number <span className="text-red-500">*</span></p>
                    </div>

                    <div className="sm:col-span-4 mt-2">
                        <div className="flex">
                            <input
                                type="text"
                                name="AWBNumber"
                                id="AWBNumber"
                                autoComplete="AWBNumber"
                                className="flex-1 border-0 focus:outline-none focus:ring-slate-500 focus:border-slate-500 rounded-md shadow-sm ring-1 ring-slate-400 bg-transparent py-1.5 px-4 text-gray-900 placeholder:text-gray-400 sm:text-md sm:leading-6"
                                placeholder="Enter Your AWB Number"
                                onChange={(e) => {
                                    const cleanedValue = e?.target?.value?.replace(/\s+/g, '');
                                    formik.setFieldValue('AWBNumber', cleanedValue);
                                }}
                                onBlur={formik?.handleBlur}
                                value={formik?.values?.AWBNumber}
                            />
                        </div>
                        {formik.touched.AWBNumber && formik.errors.AWBNumber ? (
                            <div className="text-red-500 text-sm mt-2">{formik.errors.AWBNumber}</div>
                        ) : null}
                    </div>
                    {error && <div className="text-red-500 mt-1">{error}</div>}

                    <button
                        type="submit"
                        className="mt-5 w-full text-center rounded-md bg-slate-900 py-2 text-md font-semibold text-white shadow-sm hover:bg-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                        // disabled={!formik.isValid || isLoading}
                        disabled={!formik.isValid || isLoading || globalTrackingLoading}
                    >
                        {isLoading || globalTrackingLoading ? (
                            <div className='flex justify-center items-center'>
                                <span className='mr-2'>Loading...</span>
                                <SpinLoader />
                            </div>
                        ) : (
                            'Track Now'
                        )}
                    </button>
                    <p className="text-center mt-4 text-sm font-semibold lg:mt-10 xl:mt-5">Ship and track from the comfort of your home</p>
                </form>
            </div>
            <div className="w-full lg:w-1/2 bg-white p-6 md:rounded-r-lg shadow-md mt-6 lg:mt-0">
                <p className="font-semibold mb-6">What’s your Shipment status?</p>
                <div className="mb-4">
                    <h3 className="font-semibold text-slate-800 text-sm">Shipment Picked</h3>
                    <p className="text-gray-500 text-sm">Your Shipment has been picked up.</p>
                </div>
                <div className="mb-4">
                    <h3 className="font-semibold text-slate-800 text-sm">Shipment In Transit</h3>
                    <p className="text-gray-500 text-sm">Your Shipment is on it’s way to your customer’s address</p>
                </div>
                <div className="mb-4">
                    <h3 className="font-semibold text-slate-800 text-sm">Out For Delivery</h3>
                    <p className="text-gray-500 text-sm">The Shipment executive is on its way to deliver the Shipment at your Delivery Address</p>
                </div>
                <div className="mb-4">
                    <h3 className="font-semibold text-slate-800 text-sm">Delivered</h3>
                    <p className="text-gray-500 text-sm">Your Shipment has been delivered</p>
                </div>
            </div>
        </>
    );
};

export default TrackForm;
